<script setup lang="ts">
  import { computed, defineComponent, ref } from 'vue'
  import { X, Plus, Trash2 } from 'lucide-vue-next'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogClose
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { useUserStore } from '@/store/userStore'
  import { UsersService } from '@/types/generated/services.gen'
  import type { CaseDashboardWidgetsEnum } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  // Import widget components (same as in the dashboard)
  import ClientDataWidget from '@/components/app/cases/dashboard-widgets/ClientDataWidget.vue'
  import CaseStatusWidget from '@/components/app/cases/dashboard-widgets/CaseStatusWidget.vue'
  import ActivitiesWidget from '@/components/app/cases/dashboard-widgets/ActivitiesWidget.vue'
  import CaseDataWidget from '@/components/app/cases/dashboard-widgets/CaseDataWidget.vue'
  import AppointmentsWidget from '@/components/app/cases/dashboard-widgets/AppointmentsWidget.vue'
  import DocumentsWidget from '@/components/app/cases/dashboard-widgets/DocumentsWidget.vue'
  import NotesWidget from '@/components/app/cases/dashboard-widgets/NotesWidget.vue'
  import OpponentDataWidget from '@/components/app/cases/dashboard-widgets/OpponentDataWidget.vue'
  import LawSpecificDataWidget from '@/components/app/cases/dashboard-widgets/LawSpecificDataWidget.vue'
  import AiChatWidget from '@/components/app/cases/dashboard-widgets/AiChatWidget.vue'
  import EntitiesWidget from '@/components/app/cases/dashboard-widgets/EntitiesWidget.vue'
  import EmailsWidget from '@/components/app/cases/dashboard-widgets/EmailsWidget.vue'
  import GeneratedDocumentsWidget from '@/components/app/cases/dashboard-widgets/GeneratedDocumentsWidget.vue'
  // Define props
  const props = defineProps({
    open: {
      type: Boolean,
      required: true
    }
  })

  // Define emits
  const emit = defineEmits(['update:open'])

  // Get user store
  const userStore = useUserStore()
  const user = computed(() => userStore.user)

  // Loading state
  const isAddingWidget = ref(false)

  // Translations
  const { t } = useTranslation({
    en: {
      title: 'Add Widgets',
      addWidget: 'Add Widget',
      adding: 'Adding...',
      close: 'Close',
      noWidgetsAvailable: 'All widgets are already on your dashboard.',
      removeWidget: 'Remove widget'
    },
    de: {
      title: 'Widgets hinzufügen',
      addWidget: 'Widget hinzufügen',
      adding: 'Hinzufügen...',
      close: 'Schließen',
      noWidgetsAvailable: 'Alle Widgets sind bereits auf Ihrem Dashboard.',
      removeWidget: 'Widget entfernen'
    }
  })

  // Widget definitions with titles and components
  const allWidgetDefinitions = {
    CLIENT_DATA: {
      title: 'Mandant Daten',
      component: ClientDataWidget,
      isAI: false
    },
    CURRENT_STATE: {
      title: 'Stand der Anfrage',
      component: CaseStatusWidget,
      isAI: false
    },
    OPPONENT_DATA: {
      title: 'Gegner Daten',
      component: OpponentDataWidget,
      isAI: false
    },
    ACTIVITIES: {
      title: 'Letzte Aktivität',
      component: ActivitiesWidget,
      isAI: false
    },
    EMAILS: {
      title: 'E-Mails',
      component: EmailsWidget,
      isAI: false
    },
    APPOINTMENTS: {
      title: 'Termine',
      component: AppointmentsWidget,
      isAI: false
    },
    AI_CHAT: {
      title: 'KI Chat',
      component: AiChatWidget,
      isAI: true
    },
    LAW_SPECIFIC_DATA: {
      title: 'Rechtsspezifische Daten',
      component: LawSpecificDataWidget,
      isAI: false
    },
    DOCUMENTS: {
      title: 'Dokumente',
      component: DocumentsWidget,
      isAI: false
    },
    GENERATED_DOCUMENTS: {
      title: 'Generierte Dokumente',
      component: GeneratedDocumentsWidget,
      isAI: false
    },
    ENTITIES: {
      title: 'Beteiligte',
      component: EntitiesWidget,
      isAI: false
    },
    NOTES: {
      title: 'Notizen',
      component: NotesWidget,
      isAI: false
    },
    CASE_DATA: {
      title: 'Anfrage Daten',
      component: CaseDataWidget,
      isAI: false
    }
  }

  // Compute available widgets (those not yet on the dashboard)
  const availableWidgets = computed(() => {
    if (!user.value?.case_dashboard_widgets?.length) return Object.keys(allWidgetDefinitions)

    return Object.keys(allWidgetDefinitions).filter(
      (widgetType) =>
        !user.value.case_dashboard_widgets.includes(widgetType as CaseDashboardWidgetsEnum)
    )
  })

  // Handle adding a widget
  const addWidget = async (widgetType: string) => {
    if (!user.value?.uuid) return

    isAddingWidget.value = true
    try {
      // Create a new array with the existing widgets plus the new one
      const updatedWidgets = [
        ...(user.value.case_dashboard_widgets || []),
        widgetType as CaseDashboardWidgetsEnum
      ]

      // Calculate how many widgets will remain after this addition
      const remainingWidgetsCount = Object.keys(allWidgetDefinitions).filter(
        (widget) => !updatedWidgets.includes(widget as CaseDashboardWidgetsEnum)
      ).length

      // Update locally
      if (userStore.user) {
        userStore.user.case_dashboard_widgets = updatedWidgets
      }

      // Update on the server
      await UsersService.usersPartialUpdate({
        uuid: user.value.uuid,
        requestBody: {
          case_dashboard_widgets: updatedWidgets
        }
      })

      // Only close dialog if no more widgets are available after this addition
      if (remainingWidgetsCount === 0) {
        emit('update:open', false)
      }
    } catch (error) {
      console.error('Failed to add widget:', error)
    } finally {
      isAddingWidget.value = false
    }
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent
      class="max-h-[85vh] overflow-y-auto sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw]"
    >
      <DialogHeader>
        <DialogTitle>{{ t('title') }}</DialogTitle>
        <DialogClose
          class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity
            hover:opacity-100"
        >
          <X class="h-4 w-4" />
        </DialogClose>
      </DialogHeader>

      <!-- Widget Grid -->
      <div
        v-if="availableWidgets.length > 0"
        class="mt-4 columns-1 gap-4 md:columns-2"
      >
        <div
          v-for="widgetType in availableWidgets"
          :key="widgetType"
          class="relative mb-4 inline-block w-full break-inside-avoid-column rounded-xl border border-gray-200
            bg-white shadow-sm"
        >
          <!-- Widget Header -->
          <div
            class="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-3"
          >
            <div class="flex items-center gap-2">
              <h3 class="text-base font-medium text-gray-800">
                {{ allWidgetDefinitions[widgetType].title }}
              </h3>
              <div
                v-if="allWidgetDefinitions[widgetType].isAI"
                class="flex h-6 w-7 items-center justify-center rounded bg-purple-100 text-xs font-medium text-purple-800"
              >
                KI
              </div>
            </div>
            <Button
              variant="secondary"
              :disabled="isAddingWidget"
              class="h-8 items-center gap-1 bg-purple-600 text-sm font-medium text-white hover:bg-purple-700"
              @click="addWidget(widgetType)"
            >
              <Plus
                v-if="!isAddingWidget"
                class="mr-1 h-4 w-4"
              />
              <span
                v-if="isAddingWidget"
                class="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
              ></span>
              {{ isAddingWidget ? t('adding') : t('addWidget') }}
            </Button>
          </div>

          <!-- Widget Preview -->
          <div class="h-auto p-5">
            <component :is="allWidgetDefinitions[widgetType].component" />
          </div>
        </div>
      </div>

      <div
        v-else
        class="py-8 text-center text-gray-500"
      >
        {{ t('noWidgetsAvailable') }}
      </div>
    </DialogContent>
  </Dialog>
</template>
